import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard, StaticSection } from '../components/StaticPageShell';

const About = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const pillars = [
        {
            icon: '🎯',
            titleKey: 'page.about.mission.title',
            titleFb: 'Our mission',
            textKey: 'page.about.mission.text',
            textFb:
                'Petito helps Belgian families give baby clothes, strollers, toys, and nursery furniture a second life - saving money, reducing waste, and building a supportive parent community.',
        },
        {
            icon: '🔄',
            titleKey: 'page.about.how.title',
            titleFb: 'How it works',
            textKey: 'page.about.how.text',
            textFb:
                "List items you no longer need, discover great deals near you, and connect with other parents on Petito. Our My Journey tool tracks your child's growth and suggests what you might need next.",
        },
        {
            icon: '🛡️',
            titleKey: 'page.about.safety.title',
            titleFb: 'Safety & trust',
            textKey: 'page.about.safety.text',
            textFb:
                'We enforce strict community rules: no recalled products, honest descriptions, and email-confirmed accounts. Our team reviews flagged listings and supports buyers and sellers across Belgium.',
        },
        {
            icon: '🌱',
            titleKey: 'page.about.sustainability.title',
            titleFb: 'Sustainability',
            textKey: 'page.about.sustainability.text',
            textFb:
                "Every item reused keeps textiles and plastic out of Belgian landfills. Together we're making circular parenting the norm in Flanders, Wallonia, and Brussels.",
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.about.eyebrow', 'About us')}
            title={tr('page.about.title', 'About Petito')}
            subtitle={tr('page.about.tagline', "Belgium's trusted marketplace for pre-loved baby & kids gear.")}
            maxWidth="4xl"
        >
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                {pillars.map((p) => (
                    <StaticSection key={p.titleKey} icon={p.icon} title={tr(p.titleKey, p.titleFb)}>
                        <p>{tr(p.textKey, p.textFb)}</p>
                    </StaticSection>
                ))}
            </div>

            <StaticCtaCard>
                <p className="mb-3 font-semibold sm:text-lg">
                    {tr('page.about.contact.title', 'Get in touch')}
                </p>
                <p className="mb-4 opacity-90">
                    {tr(
                        'page.about.contact.text',
                        "Questions, partnerships, or press enquiries? Reach us via the helpdesk or email support@petito.be. We're based in Belgium and reply in Dutch, French, or English."
                    )}
                </p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                    <Link
                        to="/helpdesk"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto"
                    >
                        {tr('page.faq.contact.link', 'Contact our helpdesk')}
                    </Link>
                    <Link
                        to="/partner"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] bg-white/60 px-6 py-2.5 text-sm font-semibold text-[var(--text-color)] transition hover:bg-white/90 sm:w-auto"
                    >
                        {tr('page.about.partner.link', 'Partner with us')}
                    </Link>
                </div>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default About;
